import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIndustry,
  faPlus,
  faPen,
  faLocationDot,
  faIdCard,
} from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { useAllCountries } from 'modules/Common/utils/Utility';
import CreateEditOperatorInstallation from 'modules/OperatorInstallations/CreateEditModal/CreateEditModal';
import useOperatorInstallationsStore from 'modules/OperatorInstallations/stores/operatorInstallationsStore';
import CgCnGoodHoverText from 'modules/Common/entities/CgCnGoodHoverText';
import { CgSkeletonBar } from 'modules/Common/ui/CgSkeletons';
import SupplierEmissionsModal from '../components/SupplierEmissionsModal';
import { useMockData } from '../state/MockDataContext';
import GapBadge from '../components/gaps/GapBadge';

// One pulsing stand-in for an installation card, mirroring the real card's
// geometry below (icon block, name + location lines, goods chips, footer row)
// so the grid doesn't reflow when the real cards arrive.
function InstallationCardSkeleton() {
  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-5 flex flex-col gap-3"
      aria-hidden="true"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-100 shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0">
          <CgSkeletonBar className="h-3.5 w-40 max-w-full" />
          <CgSkeletonBar className="h-2.5 w-32 max-w-full mt-2" />
          <CgSkeletonBar className="h-2.5 w-44 max-w-full mt-1.5" />
        </div>
        <div className="w-7 h-7 rounded-lg bg-slate-100 shrink-0 animate-pulse" />
      </div>
      <div className="flex items-center gap-1.5">
        <div className="h-5 w-16 rounded-full bg-slate-200 animate-pulse" />
        <div className="h-5 w-20 rounded-full bg-slate-200 animate-pulse" />
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3 mt-auto">
        <CgSkeletonBar className="h-2.5 w-24" />
        <div className="h-[30px] w-36 rounded-xl bg-slate-200 animate-pulse" />
      </div>
    </div>
  );
}

// The free-supplier Installations page keeps the Fable card layout but REUSES
// the real operator installation modal (CreateEditOperatorInstallation, V2 form
// + CountrySelectField) and the real emissions modal (OperatorEmissionsModal,
// V2 form + CnGoodField/ProductionMethodsTab, SEFA included) — per D48. No
// hand-rolled/default-HTML forms.
export default function InstallationsPage() {
  const { t } = useTranslation('freeSupplier');
  const { data, gaps, operator, installationDocs, loading } = useMockData();
  const queryClient = useQueryClient();
  const openCreateModal = useOperatorInstallationsStore((s) => s.openCreateModal);
  const openEditModal = useOperatorInstallationsStore((s) => s.openEditModal);
  // The installation whose "Add emissions data" modal is open (null = closed).
  const [emissionsInstallation, setEmissionsInstallation] = useState(null);

  const countries = useAllCountries();
  const countryNameById = useMemo(
    () => new Map(countries.map((c) => [String(c._id), c.name])),
    [countries],
  );
  // Real installation documents, keyed by id — the real modals seed from the
  // full document shape (address/authorizedRep), not the flattened card model.
  const docById = useMemo(
    () => new Map((installationDocs || []).map((d) => [String(d._id), d])),
    [installationDocs],
  );

  const factorsFor = (instId) => data.emissionFactors.filter((f) => f.installationId === instId);
  const goodsFor = (instId) => [...new Set(factorsFor(instId).map((f) => f.cnCode))];

  const refreshInstallation = (installationId) => {
    if (installationId) {
      queryClient.invalidateQueries({ queryKey: ['singleInstallation', String(installationId)] });
    }
    queryClient.invalidateQueries({ queryKey: ['operatorInstallations'] });
    queryClient.invalidateQueries({ queryKey: ['installationEmissions'] });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 m-0">
            {t('freeSupplier:installations.title', 'Installations')}
          </h1>
          <p className="text-sm text-slate-500 mt-1 m-0">
            {t(
              'freeSupplier:installations.subtitle',
              'The production sites behind your CBAM goods.',
            )}
          </p>
        </div>
        <CgRadixButton
          variant="primary"
          icon={faPlus}
          onClick={() => openCreateModal()}
        >
          {t('freeSupplier:installations.addInstallation', 'Add installation')}
        </CgRadixButton>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* While the installations query is in flight `data.installations` is
            an empty array, which would render the "No installations yet." card
            below — telling a supplier who HAS sites that they have none. Show
            the card silhouette instead until the fetch resolves. */}
        {loading && Array.from({ length: 2 }).map((_, i) => <InstallationCardSkeleton key={i} />)}

        {!loading &&
          data.installations.map((inst) => {
            const goods = goodsFor(inst.id);
            const factorCount = factorsFor(inst.id).length;
            const instGaps = gaps.filter((g) => g.scope.installationId === inst.id);
            return (
              <div
                key={inst.id}
                className="rounded-2xl bg-white border border-slate-200 p-5 flex flex-col gap-3"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primaryColor/10 flex items-center justify-center shrink-0">
                    <FontAwesomeIcon
                      icon={faIndustry}
                      className="text-primaryColor"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 m-0 flex items-center gap-2">
                      {inst.name}
                      <GapBadge gaps={instGaps} />
                    </p>
                    <p className="text-xs text-slate-400 m-0 mt-0.5 flex items-center gap-1.5">
                      <FontAwesomeIcon
                        icon={faLocationDot}
                        className="text-[10px]"
                      />
                      {inst.city ? `${inst.city}, ` : ''}
                      {countryNameById.get(String(inst.country)) || inst.country || ''}
                      {inst.unlocode ? ` · ${inst.unlocode}` : ''}
                    </p>
                    {(inst.internalId || inst.cbamRegistryId) && (
                      <p className="text-[11px] text-slate-400 m-0 mt-0.5 flex items-center gap-1.5">
                        <FontAwesomeIcon
                          icon={faIdCard}
                          className="text-[10px]"
                        />
                        {inst.internalId || ''}
                        {inst.internalId && inst.cbamRegistryId ? ' · ' : ''}
                        {inst.cbamRegistryId
                          ? t('freeSupplier:installations.cbamRegistry', 'CBAM Registry: {{id}}', {
                              id: inst.cbamRegistryId,
                            })
                          : ''}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => openEditModal(docById.get(String(inst.id)) || { _id: inst.id })}
                    className="text-slate-400 hover:text-slate-600 bg-transparent border-0 p-1"
                    title={t('freeSupplier:installations.editInstallation', 'Edit installation')}
                  >
                    <FontAwesomeIcon
                      icon={faPen}
                      className="text-xs"
                    />
                  </button>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {goods.map((g) => (
                    <span
                      key={g}
                      className="text-[11px] text-slate-600 bg-slate-100 rounded-full px-2 py-0.5"
                    >
                      <CgCnGoodHoverText
                        cnCode={g}
                        useAutonomousData
                        variant="plain"
                        triggerClassName="!text-[11px] !font-normal"
                      />
                    </span>
                  ))}
                  {goods.length === 0 && (
                    <span className="text-[11px] text-slate-400">
                      {t(
                        'freeSupplier:installations.noGoods',
                        'No goods yet — add emissions data to list goods.',
                      )}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3 mt-auto">
                  <span className="text-xs text-slate-400">
                    {t('freeSupplier:installations.emissionsRecordCount', {
                      count: factorCount,
                      defaultValue: '{{count}} emissions record',
                      defaultValue_other: '{{count}} emissions records',
                    })}
                  </span>
                  <span className="flex items-center gap-2">
                    <Link
                      to="/supplier/emission-factors"
                      className="text-xs font-semibold text-primaryColor no-underline hover:underline"
                    >
                      {t('freeSupplier:installations.viewEmissionsData', 'View emissions data →')}
                    </Link>
                    <CgRadixButton
                      variant="outline"
                      size="sm"
                      icon={faPlus}
                      onClick={() =>
                        setEmissionsInstallation(docById.get(String(inst.id)) || { _id: inst.id })
                      }
                    >
                      {t('freeSupplier:installations.addEmissionsData', 'Add emissions data')}
                    </CgRadixButton>
                  </span>
                </div>
              </div>
            );
          })}

        {!loading && data.installations.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-slate-300 p-10 text-center">
            <p className="text-sm text-slate-500 m-0">
              {t('freeSupplier:installations.emptyState.title', 'No installations yet.')}
            </p>
            <p className="text-xs text-slate-400 mt-1 m-0">
              {t(
                'freeSupplier:installations.emptyState.description',
                'Add your first production site to start sharing data.',
              )}
            </p>
          </div>
        )}
      </div>

      {/* Real operator installation create/edit modal (store-driven, V2 form). */}
      <CreateEditOperatorInstallation
        canCreate
        canManage
      />

      {/* Real operator emissions modal — SEFA included (the factor the supplier
          shares). Invalidates the single-installation cache the provider reads. */}
      {emissionsInstallation && (
        <SupplierEmissionsModal
          show={!!emissionsInstallation}
          onClose={() => setEmissionsInstallation(null)}
          operator={operator}
          installation={emissionsInstallation}
          selectedEmission={null}
          handleRefresh={() => refreshInstallation(emissionsInstallation._id)}
        />
      )}
    </div>
  );
}
